import axios from 'axios';
import { API_BASE_URL } from '../utils/api';

const TOKEN_KEY = 'client_access_token';
const CLIENT_KEY = 'client_portal_user';

export const getClientToken = () => localStorage.getItem(TOKEN_KEY) || '';
export const setClientToken = (token: string) => localStorage.setItem(TOKEN_KEY, token);
export const clearClientSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(CLIENT_KEY);
};
export const getCachedClient = () => {
  const raw = localStorage.getItem(CLIENT_KEY);
  return raw ? JSON.parse(raw) : null;
};
export const setCachedClient = (client: any) => {
  localStorage.setItem(CLIENT_KEY, JSON.stringify(client));
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getClientToken();
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearClientSession();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/client/login')) {
        window.location.href = '/client/login';
      }
    }
    return Promise.reject(err);
  },
);

export type PortalQuote = {
  id: string;
  quoteNumber: string;
  jobTitle: string;
  amount: number;
  vatAmount: number;
  totalAmount: number;
  status: string;
  validUntil: string;
  sentAt?: string | null;
  acceptedAt?: string | null;
  createdAt: string;
  publicToken?: string | null;
};

export type PortalJob = {
  id: string;
  title: string;
  status: string;
  scheduledDate?: string | null;
  completedDate?: string | null;
  address?: string | null;
  postcode?: string | null;
  assignedEngineer?: { name: string; phone?: string | null; email?: string | null } | null;
  site?: { id: string; name: string; address?: string | null; postcode?: string | null } | null;
};

export type PortalCertificate = {
  id: string;
  certificateNumber: string;
  type: string;
  status: string;
  issueDate: string;
  expiryDate?: string | null;
  outputPdfPath?: string | null;
  site?: { name: string; address?: string | null; postcode?: string | null } | null;
};

export type PortalInvoice = {
  id: string;
  invoiceNumber: string;
  status: string;
  amount: number;
  vatAmount: number;
  totalAmount: number;
  dueDate: string;
  paidDate?: string | null;
  createdAt: string;
};

export type PortalSite = {
  id: string;
  name: string;
  address?: string | null;
  postcode?: string | null;
  notes?: string | null;
  createdAt: string;
};

export type PortalOverview = {
  client: any;
  company: any;
  counts: { quotes: number; openJobs: number; outstandingInvoices: number };
  nextAppointment: any | null;
};

export const clientPortalService = {
  async resolveInvite(token: string) {
    const res = await api.get<{ success: boolean; data: { email: string; name: string | null; companyName: string | null; companyLogoUrl: string | null } }>(
      `/client-portal/public/invite/${encodeURIComponent(token)}`,
    );
    return res.data.data;
  },
  async acceptInvite(email: string, token: string, password: string) {
    const res = await api.post('/client-portal/public/accept-invite', { email, token, password });
    return res.data as { success: boolean; access_token: string; client: any };
  },
  async login(email: string, password: string) {
    const res = await api.post('/client-portal/public/login', { email, password });
    return res.data as { success: boolean; access_token: string; client: any };
  },
  async getMe() {
    const res = await api.get('/client-portal/me');
    return res.data as { client: any; user: any };
  },
  async getOverview() {
    const res = await api.get<PortalOverview>('/client-portal/overview');
    return res.data;
  },
  async listQuotes() {
    const res = await api.get<PortalQuote[]>('/client-portal/quotes');
    return res.data;
  },
  async listJobs() {
    const res = await api.get<PortalJob[]>('/client-portal/jobs');
    return res.data;
  },
  async listCertificates() {
    const res = await api.get<PortalCertificate[]>('/client-portal/certificates');
    return res.data;
  },
  async listInvoices() {
    const res = await api.get<PortalInvoice[]>('/client-portal/invoices');
    return res.data;
  },
  async listSites() {
    const res = await api.get<PortalSite[]>('/client-portal/sites');
    return res.data;
  },
  async addSite(body: { name: string; address?: string; postcode?: string; notes?: string }) {
    const res = await api.post<PortalSite>('/client-portal/sites', body);
    return res.data;
  },
};
